import React from 'react';
import { BsCheckCircleFill, BsCircle, BsX } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Divider, Flex, HStack, Icon, IconButton, Text } from '@chakra-ui/react';

import { CypressIds } from '../../../constants/cypressIds';
import { CertSubmissionDuskIds } from '../../../constants/duskIds';
import { hideModal, showModal } from '../../../redux/modules/modal';
import colors from '../../../styles/theme/foundation/colors';
import CertificateCreationBlueprintType from '../../../types/CertificateCreationBlueprintType';
import ConfirmActionWarningModal from '../../ConfirmActionWarningModal';
import StackedButtonGroup from '../../Modal/ModalButtonGroups/StackedButtonGroup';

import FakeUploadProgressBar from './FakeUploadProgressBar';
import FileDropzone from './FileDropZone';
import ReviewDocumentModal from './ReviewDocumentModal';
import TotalDocumentSizeExceededModal from './TotalDocumentSizeExceededModal';

const UploadStep = ({ blueprint }) => {
  const dispatch = useDispatch();
  const { id: recordId } = useSelector(blueprint.selectInsuranceRequirement);
  const documents = useSelector(blueprint.selectAllDocuments);
  const contentHeight = useSelector(blueprint.selectContentHeight);
  const documentRequirements = useSelector(blueprint.selectDocumentRequirements);
  const missingDocTypeIds = useSelector(blueprint.selectMissingDocumentTypeIds);
  const missingPolicyTypeIds = useSelector(blueprint.selectMissingPolicyTypeIds);
  const minimumRequirements = useSelector(blueprint.selectMinimumRequirements);
  const selectedAdditionalPolicyTypes = useSelector(blueprint.selectSelectedAdditionalPolicyTypes);
  const documentIsBeingPrepared = useSelector(blueprint.selectPreparingDocument);
  const policyRequirements = useSelector(blueprint.selectRequirementPolicyTypes);
  const { chooseAnyPolicyType, requireAgentDetails } = useSelector(blueprint.selectOptions);
  const aiScanResults = useSelector(blueprint.selectAiScanResults);

  const handleDocAdded = files => {
    dispatch(showModal(
      () => (
        <ReviewDocumentModal
          blueprint={blueprint}
          documentRequirements={documentRequirements}
          file={files[0]}
        />
      ),
      {
        size: '6xl',
        variant: 'unstyled'
      }
    ));
  };

  const handleDocRemove = doc => {
    const documentIndex = documents.indexOf(doc);
    dispatch(blueprint.removeDocument(documentIndex));
  };

  const handleBackButton = () => {
    let count = 1;

    if (requireAgentDetails) {
      count = 1;
    } else if (!requireAgentDetails && chooseAnyPolicyType) {
      count = 2;
    } else if (!requireAgentDetails && !chooseAnyPolicyType) {
      count = 3;
    }

    dispatch(blueprint.goBack(count));
  };

  const handleContinueClick = async () => {
    if (aiScanResults !== null && documents.length > 0) {
      dispatch(showModal(
        () => (
          <ConfirmActionWarningModal
            description="PINS has already extracted policy data from your documents.  Would you like to rescan your documents and replace the policy details?"
          />
        ),
        {
          title: 'Scan Again?',
          size: 'md',
          buttons: {
            component: StackedButtonGroup,
            primaryButtonOptions: {
              label: 'Rescan Documents',
              isDisabled: true,
              onClick: async () => {
                dispatch(blueprint.aiScanDocuments());
                dispatch(hideModal());
                dispatch(blueprint.nextStep(1));
              }
            },
            secondaryButtonOptions: {
              secondaryLabel: 'Keep My Scan',
              secondaryClick: async () => {
                dispatch(hideModal());
                dispatch(blueprint.nextStep(2));
              }
            }
          }
        }
      ));

      return;
    }

    const maxTotalDocSizeForAi = 100 * 1024 * 1024; // 100 MB
    const totalDocumentSize = documents.reduce((acc, doc) => acc + doc.size, 0);

    if (totalDocumentSize > maxTotalDocSizeForAi) {
      dispatch(showModal(
        () => (
          <TotalDocumentSizeExceededModal
            blueprint={blueprint}
            totalDocSize={totalDocumentSize}
          />
        ),
        {
          size: '4xl',
          title: 'Total Document Size Exceeded'
        }
      ));

      return;
    }

    let count = 1;

    if ((!selectedAdditionalPolicyTypes?.length && !policyRequirements.length)) {
      count = 3;
    } else if (!documents.length) {
      count = 2;
    }

    dispatch(blueprint.aiScanDocuments());
    dispatch(blueprint.nextStep(count));
  };

  // TODO: add ability to upload multiple documents at once
  return (
    <Flex flexFlow="row wrap" minH={contentHeight} overflow="auto" width="100%">
      <Flex backgroundColor="backgroundScale.50" direction="column" flexBasis="60%" px={6}>
        <Text mt="2.75rem" textStyle="displayXs">Submit Documents</Text>
        <Flex
          alignItems="center"
          direction="column"
          height="100%"
          justifyContent="center"
        >
          <Flex
            alignItems="center"
            backgroundColor="white"
            borderRadius="xl"
            boxShadow="soft1"
            justifyContent="center"
            mb={4}
            p="30px"
          >
            <FileDropzone onFilesAdded={handleDocAdded} />
          </Flex>

          <FakeUploadProgressBar
            isComplete={!documentIsBeingPrepared}
            isUploading={documentIsBeingPrepared}
            label="Uploading document..."
            onDone={() => {}}
          />

          {documents && (
            <>
              {Object.values(documents)
                .map((doc, index) => (
                  <Flex
                    alignItems="center"
                    background="gradient.primary"
                    borderRadius="4xl"
                    color="white"
                    flexFlow="row wrap"
                    height="52px"
                    justifyContent="space-between"
                    key={`${doc.name}-${index}`}
                    minW="405px"
                    my={2}
                    px={4}
                    py={2}
                    textStyle="bodyMd500"
                  >
                    <Text>{doc.name}</Text>
                    <IconButton
                      aria-label="close"
                      icon={<Icon as={BsX} boxSize="18px" />}
                      size="xs"
                      variant="textWhite"
                      onClick={() => handleDocRemove(doc)}
                    />
                  </Flex>
                ))}
            </>
          )}
        </Flex>

      </Flex>

      <Flex
        backgroundColor="white"
        direction="column"
        flexBasis="40%"
        justifyContent="space-between"
        px={6}
      >
        <Box minW="100%" mt="4.5rem" textAlign="left">
          <Text mb={2} textStyle="bodyXxl600">Required Documents Checklist</Text>
          <Divider borderBottomWidth="3px" />
          {documentRequirements.map(docReq => {
            const satisfied = !missingDocTypeIds.includes(docReq.insuranceDocumentTypeId);
            const icon = satisfied ? BsCheckCircleFill : BsCircle;

            return (
              <HStack alignItems="flex-start" key={docReq.insuranceDocumentTypeId} my={8}>
                <Icon as={icon} boxSize={6} color={colors.primaryScale['600']} />
                <Box>
                  <Text textStyle="bodyMd500">{docReq.insuranceDocumentTypeDisplayName}</Text>
                  {satisfied && documents
                    .filter(document => document.linkedDocTypeIds
                      .find(docTypeId => docTypeId === docReq.insuranceDocumentTypeId))
                    .map(document => (
                      <Text
                        color={colors.primaryScale['600']}
                        key={document.name}
                        textStyle="bodySm600"
                      >
                        {document.name}
                      </Text>
                    ))}
                </Box>
              </HStack>
            );
          })}
        </Box>

        <Box minW="100%" mt={8} textAlign="left">
          <Text mb={2} textStyle="bodyXxl600">Required Policies Checklist</Text>
          <Divider borderBottomWidth="3px" />
          {Object.values(minimumRequirements)
            .map(({
              policyTypeDisplayName,
              policyTypeId
            }) => {
              const satisfied = !missingPolicyTypeIds.includes(policyTypeId);
              const icon = satisfied ? BsCheckCircleFill : BsCircle;

              return (
                <HStack alignItems="flex-start" key={policyTypeId} my={6}>
                  <Icon as={icon} boxSize={6} color={colors.primaryScale['600']} />
                  <Box>
                    <Text textStyle="bodyMd500">{policyTypeDisplayName}</Text>
                  </Box>
                </HStack>
              );
            })}
        </Box>

        {!!selectedAdditionalPolicyTypes.length && (
          <Box minW="100%" mt={8} textAlign="left">
            <Text mb={2} textStyle="bodyXxl600">Additional Policies</Text>
            <Divider borderBottomWidth="3px" />
            {selectedAdditionalPolicyTypes.map(type => (
              <HStack alignItems="flex-start" key={type.id} my={6}>
                <Icon as={BsCheckCircleFill} boxSize={6} color={colors.primaryScale['600']} />
                <Box>
                  <Text textStyle="bodyMd500">{type.displayName}</Text>
                </Box>
              </HStack>
            ))}
          </Box>
        )}
        <Flex justifyContent="space-between" my={8}>
          <Button
            variant="secondary"
            onClick={handleBackButton}
          >
            Back
          </Button>
          <Button
            data-cy={CypressIds.certUploadContinueButton}
            dusk={CertSubmissionDuskIds.continueFromDocumentSelectionBox}
            isLoading={documentIsBeingPrepared}
            variant="gradientPrimary"
            onClick={handleContinueClick}
          >
            Continue
          </Button>
        </Flex>
      </Flex>
    </Flex>

  );
};

UploadStep.propTypes = { blueprint: CertificateCreationBlueprintType };

export default UploadStep;
